import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';
import useIdleTimer from '../hooks/useIdleTimer';

// Common practice for financial platforms, previously missing here entirely: the access
// token silently refreshes on every request regardless of whether the user is actually at
// the keyboard, so a tab left open stayed logged in indefinitely. Web only — the native app
// gets its own re-auth via BiometricLockGate.js's Face ID/Touch ID relock, and force-logging
// out there too would be redundant on top of it. client/src/pages/Help.js documents this
// behavior in the session-length FAQ entry.
export const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
export const IDLE_WARNING_MS = 60 * 1000;

export default function IdleLogoutGate() {
  const { isAuthenticated, logout } = useAuth();
  const enabled = isAuthenticated && !Capacitor.isNativePlatform();
  const { warning, secondsRemaining, stayLoggedIn } = useIdleTimer({
    idleMs: IDLE_TIMEOUT_MS,
    warningMs: IDLE_WARNING_MS,
    onTimeout: logout,
    enabled,
  });

  const stayButtonRef = useRef(null);
  useEffect(() => {
    if (warning) stayButtonRef.current?.focus();
  }, [warning]);

  if (!warning) return null;

  return (
    <div className="idle-logout-overlay" role="dialog" aria-modal="true" aria-label="Session timeout warning">
      <div className="idle-logout-modal">
        <p style={{ fontWeight: 600, fontSize: '1rem', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>You've been inactive</p>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
          For your security, you'll be logged out in <span aria-live="polite">{secondsRemaining}s</span>.
        </p>
        <button ref={stayButtonRef} type="button" onClick={stayLoggedIn} className="btn-primary" style={{ width: '100%' }}>
          Stay logged in
        </button>
      </div>
    </div>
  );
}
